// Pure format/time helpers shared by the results template, the other fcb
// templates and the image-editor previews. They live here rather than in
// Index.jsx so ResultOverlay can use them without importing its own parent.

// Bottom scrim darkening the foot of the format so the score block stays
// readable. It belongs to the frame, not to the picture: painting it inside
// each background layer made it ride up with a layer that was dragged or
// sized to something shorter than the format, leaving the score on bare image
// and a dark band across the middle. Other fcb templates do not share these
// stops, so this ramp is ours.
export const LAYER_BOTTOM_GRADIENT =
  "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(20, 20, 20, 0.7) 40%, rgba(30,30,30,1) 100%)";

// How far up the format the ramp reaches, as a percentage of the frame. The
// editor exposes this as a slider (effects.scrimHeight); 65 is what it was
// fixed at before, so anything saved without the key keeps its old look.
export const DEFAULT_SCRIM_HEIGHT = 65;
export const MIN_SCRIM_HEIGHT = 0;
export const MAX_SCRIM_HEIGHT = 100;

export const clampScrimHeight = (height) => {
  // `null` and `""` have to be caught before Number(), which turns both into 0
  // -- a saved template with no value for this would lose its ramp entirely.
  if (height === null || height === undefined || height === "") {
    return DEFAULT_SCRIM_HEIGHT;
  }
  const n = Number(height);
  if (!Number.isFinite(n)) return DEFAULT_SCRIM_HEIGHT;
  return Math.min(MAX_SCRIM_HEIGHT, Math.max(MIN_SCRIM_HEIGHT, n));
};

// Used by the template and by the editor's preview of it, so the canvas and
// the export darken the same band, at the same height. z-index 2 keeps it over
// the layers and the UEFA gradient, which is where the per-layer copies used to
// sit -- and it stays *under* the score block and crests only because
// `.custom-background-container` isolates its stacking context (see
// ResultsNew/style/style.scss). Drop that isolation and this 2 escapes into the
// template block, where it outranks the overlay and greys out the content the
// ramp is there to lift.
export const bottomScrimStyle = (height = DEFAULT_SCRIM_HEIGHT) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: `${clampScrimHeight(height)}%`,
  background: LAYER_BOTTOM_GRADIENT,
  pointerEvents: "none",
  zIndex: 2,
});

// The unadjusted ramp, kept for callers that have no effects to read from.
export const BOTTOM_SCRIM_STYLE = bottomScrimStyle(DEFAULT_SCRIM_HEIGHT);

export const sortByTimeFirst = (arr) => {
  const getMinute = (timeStr) => {
    if (!timeStr) return 0;
    const base = timeStr.split('+')[0];
    return parseInt(base, 10);
  };

  return arr.sort((a, b) => {
    return getMinute(a.times[0]) - getMinute(b.times[0]);
  });
};

export const ratioFromFormat = (format) => {
  // return 0;
  if (format === '1920x1080') return 1.78;
  if (format === '1200x1500') return 0.8;
  return 0.56;
}

export const formatFromRatio = (ratio) => {
  if (ratio === 1.78) return '1920x1080';
  if (ratio === 0.8) return '1200x1500';
  return '1080x1920';
}

export const ratioStringFromFormat = (format) => {
  if (format === '1920x1080') return '16/9';
  if (format === '1200x1500') return '4/5';
  return '9/16';
}

export const parseMinute = (min) => {
  const s = String(min);
  if (s.includes('+')) {
    const [main, extra] = s.split('+').map(Number);
    return (Number.isNaN(main) ? 0 : main) * 100 + (Number.isNaN(extra) ? 0 : extra);
  }
  const n = Number(s);
  return (Number.isNaN(n) ? 0 : n) * 100;
};

export const formatMinute = (min) => {
  const s = String(min);
  if (s.includes('+')) {
    const [main, extra] = s.split('+');
    return `${main}'+${extra}`;
  }
  if (s.includes(' (P)')) {
    const [main, extra] = s.split(' (P)');
    return `${main}' (P)`;
  }
  if (s.includes(' (OG)')) {
    const [main, extra] = s.split(' (OG)');
    return `${main}' (OG)`;
  }
  if (parseInt(min) > 90) {
    const extra = parseInt(min) - 90;
    return `90'+${extra}`;
  }
  return `${s}'`;
};
